import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import FaArrowLeft from 'react-icons/lib/ti/arrow-left';
import FaLock from 'react-icons/lib/fa/lock';
import { browserHistory } from 'react-router';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { selectAllMeState } from 'src/common/state/me/me-selectors';

import { redirectAndStoreReturnPath } from 'src/utils/redirect';
import { apiRequest } from 'src/utils/api-request';

import {
  updateCardAction,
  fetchExtraUserInfo,
} from '../../actions';
import { subscriptionActions } from '../subscription/subscription-actions';
import './settings.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY!);

// API function to create setup intent for card updates
const createCardUpdateSetupIntent = async (): Promise<{ client_secret: string; type: string }> => {
  return apiRequest<{ client_secret: string; type: string }>({
    path: '/me/card/setup-intent/create',
    method: 'POST',
  });
};

// API function to update card with setup intent
const updateCardWithSetupIntent = async (setupIntentId: string): Promise<any> => {
  return apiRequest<any>({
    path: '/me/card/setup-intent/complete',
    method: 'POST',
    body: {
      setup_intent_id: setupIntentId,
    },
  });
};

const ChangePayment: React.FC = () => {
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectAllMeState);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeSetupIntent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const setupIntent = await createCardUpdateSetupIntent();
        setClientSecret(setupIntent.client_secret);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize payment form');
      } finally {
        setIsLoading(false);
      }
    };

    initializeSetupIntent();
  }, []);

  // Handle redirect back from Stripe after setup intent confirmation
  useEffect(() => {
    const handleStripeRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const setupIntent = urlParams.get('setup_intent');
      const setupIntentClientSecret = urlParams.get('setup_intent_client_secret');
      const redirectStatus = urlParams.get('redirect_status');

      if (setupIntent && setupIntentClientSecret && redirectStatus === 'succeeded') {
        try {
          setIsLoading(true);
          setError(null);
          
          // Update the card using the confirmed setup intent
          const result = await updateCardWithSetupIntent(setupIntent);
          
          if (result.success) {
            // Refresh user data
            await dispatch(fetchExtraUserInfo('billing_info'));
            await dispatch(subscriptionActions.fetchSubscriptionInfo());
            
            // Redirect to billing page with success message
            browserHistory.push('/billing?alert=card_changed');
          } else {
            setError('Failed to update card');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to update card');
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleStripeRedirect();
  }, [dispatch]);

  // Redirect if not logged in - moved after all hooks
  if (!me.user_name) {
    redirectAndStoreReturnPath('/login');
    return null;
  }

  return (
    <>
      <div className="update-card--back">
        <a
          className="back"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            browserHistory.goBack();
          }}
        >
          <FaArrowLeft />
        </a>
      </div>
      <div className="settings">
        <Helmet>
          <title>Change Payment - Streamable</title>
          <meta name="description" content="Update your card." />
        </Helmet>
        
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
            {error}
            <button 
              onClick={() => window.location.reload()} 
              className="btn btn-primary ml-2"
            >
              Retry
            </button>
          </div>
        )}
        
        {clientSecret ? (
          <Elements 
            stripe={stripePromise} 
            options={{
              loader: 'never',
              clientSecret,
              appearance: {
                variables: {
                  colorPrimary: '#007AFF',
                  colorText: '#1C1C1E',
                  colorDanger: '#D70015',
                  colorSuccess: '#34C759',
                  fontFamily: "'Manrope', sans-serif",
                  fontSizeSm: '14px',
                },
                rules: {
                  '.Tab': {
                    border: '1px solid #E0E0E0',
                    borderRadius: '4px',
                    padding: '12px',
                    marginBottom: '12px',
                  },
                  '.Tab:hover': {
                    borderColor: '#007AFF',
                  },
                  '.Tab--selected': {
                    borderColor: '#007AFF',
                    backgroundColor: '#F0F8FF',
                  },
                  '.Input': {
                    border: '1px solid #E0E0E0',
                    borderRadius: '4px',
                    padding: '12px',
                    fontSize: '16px',
                  },
                  '.Input:focus': {
                    borderColor: '#007AFF',
                    boxShadow: '0 0 0 1px #007AFF',
                  },
                },
              },
            }}
          >
            <SecurePaymentForm clientSecret={clientSecret} isLoading={isLoading} />
          </Elements>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            {isLoading ? (
              <div>
                <div style={{ marginBottom: '20px' }}>Setting up secure payment form...</div>
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '20px' }}>Payment form not ready</div>
                <button 
                  onClick={() => window.location.reload()} 
                  className="btn btn-primary"
                >
                  Refresh Page
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        .settings {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }
        
        .update-card--back .back {
          position: absolute;
          top: 0px;
          left: 15px;
          font-size: 40px;
          color: #333;
          padding: 10px;
          z-index: 9;
        }
        
        .update-card--back .back:hover {
          color: black;
        }
        
        /* Clean, minimal styling for the payment form */
        .payment-form {
          margin-top: 20px;
          width: 100%;
        }
        
        /* Contain Stripe form to prevent overflow */
        .payment-form .card {
          /* Use existing card styles from settings.css */
          overflow: hidden;
          position: relative;
          min-height: 600px;
        }
        
        /* Ensure Stripe elements don't expand beyond container */
        .payment-form .card > div {
          min-height: 550px;
          overflow: visible;
        }
        
        /* Position button below the form */
        .payment-form__submit {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding-top: 20px;
          border-top: 1px solid #e0e0e0;
          margin-top: 20px;
          position: relative;
          z-index: 5;
        }
        
        .payment-form__security-info {
          display: flex;
          align-items: center;
          font-size: 14px;
          color: #666;
        }
        
        .payment-form__button {
          min-width: 120px;
          height: 40px;
          font-size: 16px;
          font-weight: 500;
          white-space: nowrap;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .settings {
            padding: 16px;
          }
          
          .payment-form__submit {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }
          
          .payment-form__security-info {
            justify-content: center;
          }
          
          .payment-form__button {
            width: 100%;
            min-width: auto;
          }
        }
      `}</style>
    </>
  );
};

// Secure payment form using PaymentElement
const SecurePaymentForm: React.FC<{ clientSecret: string; isLoading: boolean }> = ({ clientSecret, isLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClick = async () => {
    if (!stripe || !elements) {
      alert('The payment form is not ready yet. Please try again shortly.');
      return;
    }
    
    setIsSubmitting(true);
    dispatch(updateCardAction({ purchasing: true }));

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: '',
              email: '',
              phone: '',
              address: {
                country: 'US',
                line1: '',
                line2: '',
                city: '',
                state: '',
                postal_code: '',
              },
            },
          },
        },
        redirect: 'always',
      });

      if (error) {
        if (error.type === 'card_error' || error.type === 'validation_error') {
          dispatch(updateCardAction(new Error(`${error.message} Please try a different payment method.`)));
        } else {
          dispatch(updateCardAction(new Error('We could not process your payment. Please try a different payment method.')));
        }
        setIsSubmitting(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      dispatch(updateCardAction(new Error(errorMessage)));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="payment-form">
      <div className="card">
        <PaymentElement
          options={{
            paymentMethodOrder: ['card'],
            layout: 'tabs',
            fields: {
              billingDetails: 'never',
            },
          }}
        />
      </div>
      
      <div className="payment-form__submit">
        <span className="payment-form__security-info">
          <FaLock style={{ marginRight: '8px' }} />
          Secured with SSL encryption
        </span>
        <button
          className={`blue-button payment-form__button ${isSubmitting || isLoading ? 'disabled' : ''}`}
          disabled={isSubmitting || isLoading}
          onClick={handleClick}
        >
          {isLoading ? 'Setting up...' : 
           isSubmitting ? 'Updating...' : 
           'Update Card'}
        </button>
      </div>
    </div>
  );
};

export default ChangePayment; 